
import Vue, { PropType } from 'vue';
import {
	CRS,
	HeatLatLngTuple,
	HeatLayer,
	HeatMapOptions,
	latLng,
	LatLng,
	LatLngBounds,
	latLngBounds,
	MapOptions,
} from 'leaflet';
import { Floor } from '@/interfaces/locations';
import DeviceMarker from '@/components/common/DeviceMarker.vue';
import { StatefulDevice } from './DeviceMap.vue';
import { DeviceMarkerMap } from '@/components/configuration/DeviceMap.vue';
import { LMap } from 'vue2-leaflet';
import DateRangeInput from '@/components/common/DateRangeInput.vue';
import ColorScale from '@/components/common/ColorScale.vue';
import { SelectMenu } from '@/interfaces/global';
import dayjs from '@/plugins/dayjs';
import { OccupancyReports } from '@/interfaces/reports';
import { LocaleMessage } from 'vue-i18n';
import HeatmapIconsLegend from '@/components/configuration/HeatmapIconsLegend.vue';
import { Controller } from '@/interfaces/controllers';

interface StartEndTime {
	from: dayjs.Dayjs;
	to: dayjs.Dayjs;
}

interface ComponentData {
	zoom: number | null;
	minZoom: number | null;
	maxZoom: number | null;
	center: LatLng;
	bounds: LatLngBounds;
	crs: CRS;
	mapOptions: MapOptions;
	heatDevices: { [id: number]: number };
	heatLayer: HeatLayer | null;
	heatLayerConfig: HeatMapOptions;
	mapComponent: LMap | null;
	intervalValue: number;
	occupancyTypeValue: string;
	selectedMonth: string;
	maxHeatValue: number;
	maxMonth: string;
	validMonth: boolean;
	rangeData: {
		min: dayjs.Dayjs | null;
		max: dayjs.Dayjs | null;
		from: dayjs.Dayjs | null;
		to: dayjs.Dayjs | null;
	};
}

export default Vue.extend({
	components: { DeviceMarker, DateRangeInput, ColorScale, HeatmapIconsLegend },

	props: {
		id: { type: String, default: 'default' },
		floors: { type: Array as PropType<Floor[]>, default: (): Floor[] => [] },
		localDevices: {
			type: Array as PropType<StatefulDevice[]>,
			default: (): StatefulDevice[] => [],
		},
		controllers: { type: Array as PropType<Controller[]>, default: (): Controller[] => [] },
		selectedFloor: { type: Object as PropType<Floor> },
		occupancyEvents: {
			type: Object as PropType<{ [id: number]: OccupancyReports }>,
			default: (): { [id: number]: OccupancyReports } => ({}),
		},
		time: {
			type: Object as PropType<StartEndTime>,
			default: (): StartEndTime => ({ from: dayjs().subtract(1, 'day'), to: dayjs() }),
		},
		loading: {
			type: Boolean,
			default: false,
		},
		locale: {
			type: String,
			default: 'en-gb',
		},
	},

	data(): ComponentData {
		return {
			zoom: null,
			minZoom: 1,
			maxZoom: null,
			center: latLng(0, 0),
			bounds: latLngBounds({ lat: 100, lng: -100 }, { lat: -100, lng: 100 }),
			crs: CRS.Simple,
			mapOptions: {
				attributionControl: false,
				zoomControl: false,
			},
			heatLayer: null,
			heatDevices: {},
			heatLayerConfig: {
				radius: 35,
				blur: 10,
				maxZoom: 1,
				gradient: { 0.25: 'lime', 0.5: 'yellow', 1.0: 'red' },
			},
			mapComponent: null,
			selectedMonth: '',
			maxMonth: '',
			maxHeatValue: 100,
			validMonth: true,
			intervalValue: 86400000,
			occupancyTypeValue: 'pct_working_h',
			rangeData: {
				min: null,
				max: null,
				from: null,
				to: null,
			},
		};
	},

	computed: {
		deviceMarkers(): DeviceMarkerMap[] {
			return this.localDevices.map((device) => {
				return {
					id: device.id,
					name: this.heatDevices[device.id]
						? `${device.name} - ${this.heatDevices[device.id]}`
						: device.name,
					guid: this.controllers
						?.find((controller) => controller.id === device.fk_controller)
						?.guid.slice(-4),
					position: { lat: device.local_latitude, lng: device.local_longitude },
					visible: true,
					isLuminaire: Boolean(device.is_luminaire),
					isMovementSensor: Boolean(device.is_movement_sensor),
					isLightLevelSensor: Boolean(device.is_lightlevel_sensor),
					isGateway: false, //Gateways are not going to be listed on the map
					isOtherSensor: Boolean(device.is_other_sensor),
				};
			});
		},

		rangeValues(): dayjs.Dayjs[] {
			if (this.intervalValue === 3600000) {
				return [this.rangeData.from!];
			}
			return [this.rangeData.from!, this.rangeData.to!];
		},

		fromDateFormatted(): string {
			return this.rangeData.from!.format('DD/MM/YYYY HH:mm') ?? '-';
		},

		toDateFormatted(): string {
			return this.rangeData.to!.format('DD/MM/YYYY HH:mm') ?? '-';
		},

		intervalOptions(): SelectMenu[] {
			return [
				{
					value: 3600000,
					text: this.$t('interval_options.hour', { count: 1 }).toString(),
					disabled: false,
				},
				{
					value: 86400000,
					text: this.$t('interval_options.day', { count: 1 }).toString(),
					disabled: false,
				},
				{
					value: 604800000,
					text: this.$t('interval_options.week', { count: 1 }).toString(),
					disabled: false,
				},
			];
		},

		occupanyTypeOptions(): SelectMenu[] {
			return [
				{ value: 'pct_working_h', text: this.$i18n.t('Working hours') },
				{ value: 'pct_outside_working_h', text: this.$i18n.t('Outside working hours') },
			];
		},

		currentOccupancyText(): LocaleMessage {
			return this.occupanyTypeOptions.find(
				(occupancy) => occupancy.value === this.occupancyTypeValue
			)!.text;
		},
	},

	watch: {
		occupancyEvents: {
			async handler(occupancyEvents) {
				await this.heatListSetter(occupancyEvents);
			},
		},

		occupancyTypeValue: {
			async handler() {
				await this.heatListSetter(this.occupancyEvents);
			},
		},

		selectedMonth: {
			handler(selectedMonth) {
				this.handleMonthChange(selectedMonth);
			},
		},

		intervalValue: {
			handler(newIntervalValue, oldIntervalValue) {
				this.handleIntervalChange(newIntervalValue, oldIntervalValue);
				this.heatLayerConfig.max = this.maxHeatValue;
				if (this.heatLayer) {
					this.heatLayer.setOptions(this.heatLayerConfig);
				}
			},
		},
	},

	created() {
		this.timeFrameReady();
	},

	methods: {
		timeFrameReady(): void {
			this.selectedMonth = this.time.to.format('YYYY-MM');
			this.maxMonth = this.time.to.format('YYYY-MM');
			this.rangeData.min = this.time.from;
			this.rangeData.max = this.time.to.add(1, 'ms');
			this.rangeData.from = this.time.from;
			this.rangeData.to = this.time.to.add(this.intervalValue, 'ms');
		},

		heatMapReady(): void {
			this.mapComponent = this.$refs.heatMap as LMap;
			this.heatLayerConfig.max = this.maxHeatValue;
			this.heatLayer = window.L.heatLayer([], this.heatLayerConfig);
			this.mapComponent!.mapObject.addLayer(this.heatLayer);
		},

		handleMonthChange(selectedMonth: string): void {
			if (!dayjs(selectedMonth).isValid()) {
				this.validMonth = false;
				return;
			} else {
				this.validMonth = true;
			}

			this.rangeData.min = dayjs(selectedMonth).utc(true).startOf('month');
			this.rangeData.max = dayjs(selectedMonth).utc(true).endOf('month').add(1, 'ms');

			if (this.intervalValue === 86400000) {
				this.handleIntervalChange(86400000, 86400000);
			} else {
				this.intervalValue = 86400000;
			}
		},

		handleIntervalChange(newIntervalValue: number, oldIntervalValue: number): void {
			if (
				oldIntervalValue !== 604800000 &&
				newIntervalValue === 604800000 &&
				(this.rangeData.max!.valueOf() - this.rangeData.min!.valueOf()) % 604800000 !== 0
			) {
				const missingDays = 35 - this.rangeData.max!.subtract(1, 'ms').date();
				this.rangeData.max = this.rangeData.max!.add(missingDays, 'd');
			}

			if (oldIntervalValue === 604800000) {
				this.rangeData.max = dayjs(this.selectedMonth).utc(true).endOf('month').add(1, 'ms');
			}

			this.rangeData.from = dayjs(this.selectedMonth).utc(true).startOf('month');
			this.rangeData.to = dayjs(this.selectedMonth)
				.utc(true)
				.startOf('month')
				.add(this.intervalValue, 'ms');

			this.changeTime([this.rangeData.from, this.rangeData.to]);
		},

		changeFloor(floor: Floor): void {
			this.$emit('changeFloor', floor, 'heat');
		},

		changeOccupancyType(occupancyType: string): void {
			this.occupancyTypeValue = occupancyType;
		},

		async heatListSetter(occupancyEvents: { [id: number]: OccupancyReports }): Promise<void> {
			this.setHeatList(await this.heatListBuilder(occupancyEvents));
		},

		async heatListBuilder(occupancyEvents: {
			[id: number]: OccupancyReports;
		}): Promise<(LatLng | HeatLatLngTuple)[]> {
			const heatList: (LatLng | HeatLatLngTuple)[] = [];
			this.heatDevices = {};
			if (Object.keys(occupancyEvents).length !== 0) {
				for (const device of this.localDevices) {
					const event: OccupancyReports | undefined = occupancyEvents[device.id];
					if (!event) {
						continue;
					}
					let value: number;
					switch (this.occupancyTypeValue) {
						case 'pct_working_h':
							value = event.pct_working_h;
							break;
						case 'pct_outside_working_h':
							value = event.pct_outside_working_h;
							break;
						default:
							value = event.pct_working_h;
							break;
					}
					if (value) {
						value = Math.min(100, Math.max(0, value));
					}
					this.$set(this.heatDevices, device.id, value);
					heatList.push([device.local_latitude!, device.local_longitude!, value]);
				}
			}
			return heatList;
		},

		setHeatList(heatList: (LatLng | HeatLatLngTuple)[]): void {
			this.heatLayer!.setLatLngs(heatList);
		},

		changeTime(date: dayjs.Dayjs[]): void {
			if (this.intervalValue === 3600000) {
				this.rangeData.from = date[0];
				this.rangeData.to = date[0].add(1, 'h');
				this.$emit('changeTime', {
					from: this.rangeData.from,
					to: this.rangeData.to,
				});
			} else {
				if (!this.rangeData.from!.isSame(date[0]) || !this.rangeData.to!.isSame(date[1])) {
					this.rangeData.from = date[0];
					this.rangeData.to = date[1];
				}
				this.$emit('changeTime', {
					from: date[0],
					to: date[1],
				});
			}
		},
	},
});
